import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { X, Plus, Eye, Globe, Video, Upload, Trash2, Lock, Unlock, GripVertical, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import { COUNTRIES } from '../utils/countries';
import ProfileVideoPlayer from '../components/ProfileVideoPlayer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfileSetup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState({
    age: '',
    gender: '',
    bio: '',
    interests: [],
    location: '',
    looking_for: '',
    relationship_status: '',
    photos: [],
    date_place_photos: [],
    countries_to_visit: []
  });
  const [currentInterest, setCurrentInterest] = useState('');
  const [currentPhoto, setCurrentPhoto] = useState('');
  const [currentDatePlacePhoto, setCurrentDatePlacePhoto] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingDatePlace, setUploadingDatePlace] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  // Profile video states
  const [videoUnlockStatus, setVideoUnlockStatus] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [profileVideoUrl, setProfileVideoUrl] = useState(null);
  // Photo reordering state
  const [draggedPhotoIdx, setDraggedPhotoIdx] = useState(null);

  // Photo reordering functions
  const movePhotoUp = (idx) => {
    if (idx === 0) return;
    const newPhotos = [...profile.photos];
    [newPhotos[idx - 1], newPhotos[idx]] = [newPhotos[idx], newPhotos[idx - 1]];
    setProfile({ ...profile, photos: newPhotos });
    toast.success(`Photo moved to position ${idx}`);
  };

  const movePhotoDown = (idx) => {
    if (idx === profile.photos.length - 1) return;
    const newPhotos = [...profile.photos];
    [newPhotos[idx], newPhotos[idx + 1]] = [newPhotos[idx + 1], newPhotos[idx]];
    setProfile({ ...profile, photos: newPhotos });
    toast.success(`Photo moved to position ${idx + 2}`);
  };

  const handleDragStart = (e, idx) => {
    setDraggedPhotoIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIdx) => {
    e.preventDefault();
    if (draggedPhotoIdx === null || draggedPhotoIdx === dropIdx) return;
    
    const newPhotos = [...profile.photos];
    const draggedPhoto = newPhotos[draggedPhotoIdx];
    newPhotos.splice(draggedPhotoIdx, 1);
    newPhotos.splice(dropIdx, 0, draggedPhoto);
    
    setProfile({ ...profile, photos: newPhotos });
    setDraggedPhotoIdx(null);
    toast.success(`Photo moved to position ${dropIdx + 1}`);
  };

  const handleDragEnd = () => {
    setDraggedPhotoIdx(null);
  };

  useEffect(() => {
    loadProfile();
    loadVideoUnlockStatus();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile`, { withCredentials: true });
      const user = response.data;
      setCurrentUserId(user.id);
      setProfile({
        age: user.age || '',
        gender: user.gender || '',
        bio: user.bio || '',
        interests: user.interests || [],
        location: user.location || '',
        looking_for: user.looking_for || '',
        relationship_status: user.relationship_status || '',
        photos: user.photos || [],
        date_place_photos: user.date_place_photos || [],
        countries_to_visit: user.countries_to_visit || []
      });
      setProfileVideoUrl(user.profile_video_url);
    } catch (e) {
      console.error('Failed to load profile:', e);
    }
  };

  const loadVideoUnlockStatus = async () => {
    try {
      const response = await axios.get(`${API}/profile-video/unlock-status`, { withCredentials: true });
      setVideoUnlockStatus(response.data);
    } catch (e) {
      console.error('Failed to load video unlock status:', e);
    }
  };

  const handleVideoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (20MB)
    if (file.size > 20 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 20MB.');
      return;
    }

    // Validate file type
    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Only MP4 and WebM videos allowed.');
      return;
    }

    setUploadingVideo(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/profile-video`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProfileVideoUrl(response.data.url);
      toast.success('Profile video uploaded! Keep it under 5 seconds for best experience.');
      await loadProfile();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload video');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleDeleteVideo = async () => {
    try {
      await axios.delete(`${API}/profile-video`, { withCredentials: true });
      setProfileVideoUrl(null);
      toast.success('Profile video deleted');
      await loadProfile();
    } catch (e) {
      toast.error('Failed to delete video');
    }
  };

  const addInterest = () => {
    if (currentInterest && !profile.interests.includes(currentInterest)) {
      setProfile({ ...profile, interests: [...profile.interests, currentInterest] });
      setCurrentInterest('');
    }
  };

  const removeInterest = (interest) => {
    setProfile({ ...profile, interests: profile.interests.filter(i => i !== interest) });
  };

  const addPhoto = () => {
    if (currentPhoto && !profile.photos.includes(currentPhoto)) {
      setProfile({ ...profile, photos: [...profile.photos, currentPhoto] });
      setCurrentPhoto('');
    }
  };

  const removePhoto = (photo) => {
    setProfile({ ...profile, photos: profile.photos.filter(p => p !== photo) });
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });

      // Cloudinary returns full URLs, local storage returns relative paths
      const photoUrl = response.data.url.startsWith('http') 
        ? response.data.url 
        : `${BACKEND_URL}${response.data.url}`;
      setProfile({ ...profile, photos: [...profile.photos, photoUrl] });
      toast.success('Photo uploaded!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      event.target.value = '';
    }
  };

  const addDatePlacePhoto = () => {
    if (currentDatePlacePhoto && !profile.date_place_photos.includes(currentDatePlacePhoto)) {
      setProfile({ ...profile, date_place_photos: [...profile.date_place_photos, currentDatePlacePhoto] });
      setCurrentDatePlacePhoto('');
    }
  };

  const handleDatePlacePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingDatePlace(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload/date-place-photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });

      // Cloudinary returns full URLs, local storage returns relative paths
      const photoUrl = response.data.url.startsWith('http') 
        ? response.data.url 
        : `${BACKEND_URL}${response.data.url}`;
      setProfile({ ...profile, date_place_photos: [...profile.date_place_photos, photoUrl] });
      toast.success('Date place photo uploaded!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingDatePlace(false);
      event.target.value = '';
    }
  };

  const removeDatePlacePhoto = (photo) => {
    setProfile({ ...profile, date_place_photos: profile.date_place_photos.filter(p => p !== photo) });
  };

  const addCountry = (country) => {
    if (!profile.countries_to_visit.includes(country)) {
      setProfile({ ...profile, countries_to_visit: [...profile.countries_to_visit, country] });
    }
    setShowCountryDropdown(false);
  };

  const removeCountry = (country) => {
    setProfile({ ...profile, countries_to_visit: profile.countries_to_visit.filter(c => c !== country) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!profile.gender) {
      toast.error('Please select your gender');
      return;
    }
    
    if (!profile.age) {
      toast.error('Please enter your age');
      return;
    }
    
    setLoading(true);

    try {
      const updateData = {
        gender: profile.gender,
        bio: profile.bio,
        interests: profile.interests,
        location: profile.location,
        looking_for: profile.looking_for,
        relationship_status: profile.relationship_status,
        photos: profile.photos,
        date_place_photos: profile.date_place_photos,
        countries_to_visit: profile.countries_to_visit
      };

      // Add age (already validated above)
      updateData.age = parseInt(profile.age);

      console.log('Sending profile update with data:', updateData);
      console.log('Relationship status being sent:', profile.relationship_status);

      const response = await axios.put(`${API}/profile`, updateData, { withCredentials: true });
      console.log('Profile update response:', response.data);
      
      toast.success('Profile updated successfully!');
      navigate('/discover');
    } catch (e) {
      console.error('Profile update error:', e);
      const errorMessage = e.response?.data?.detail || e.message || 'Failed to update profile';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-8">
          <h1 className="text-4xl font-bold text-center mb-2 bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">
            Complete Your Profile
          </h1>
          <p className="text-center text-gray-600 mb-8">Tell us about yourself to find your perfect match</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="age">Age</Label>
                <Input
                  id="age"
                  data-testid="age-input"
                  type="number"
                  value={profile.age}
                  onChange={(e) => setProfile({ ...profile, age: e.target.value })}
                  required
                  className="mt-1"
                  min="18"
                  max="100"
                />
              </div>
              <div>
                <Label htmlFor="gender">Gender</Label>
                <Select value={profile.gender} onValueChange={(value) => setProfile({ ...profile, gender: value })}>
                  <SelectTrigger data-testid="gender-select" className="mt-1">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="looking_for">Looking For</Label>
              <Select value={profile.looking_for} onValueChange={(value) => setProfile({ ...profile, looking_for: value })}>
                <SelectTrigger data-testid="looking-for-select" className="mt-1">
                  <SelectValue placeholder="Select preference" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="men">Men</SelectItem>
                  <SelectItem value="women">Women</SelectItem>
                  <SelectItem value="everyone">Everyone</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="relationship_status">Relationship Status</Label>
              <Select value={profile.relationship_status} onValueChange={(value) => setProfile({ ...profile, relationship_status: value })}>
                <SelectTrigger data-testid="relationship-status-select" className="mt-1">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Single</SelectItem>
                  <SelectItem value="in_relationship">In a Relationship</SelectItem>
                  <SelectItem value="married">Married</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                data-testid="location-input"
                value={profile.location}
                onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                placeholder="City, Country"
                required
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                data-testid="bio-textarea"
                value={profile.bio}
                onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                placeholder="Tell us about yourself..."
                required
                className="mt-1 min-h-[100px]"
              />
            </div>

            <div>
              <Label>Interests</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  data-testid="interest-input"
                  value={currentInterest}
                  onChange={(e) => setCurrentInterest(e.target.value)}
                  placeholder="Add an interest"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addInterest())}
                />
                <Button type="button" onClick={addInterest} data-testid="add-interest-btn" size="icon" className="bg-purple-500 hover:bg-purple-600">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {profile.interests.map((interest, idx) => (
                  <Badge key={idx} variant="secondary" className="px-3 py-1.5 bg-purple-100 text-purple-700 hover:bg-purple-200">
                    {interest}
                    <X className="w-3 h-3 ml-2 cursor-pointer" onClick={() => removeInterest(interest)} />
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <Label>Photos</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  disabled={uploadingPhoto}
                  data-testid="photo-upload-input"
                  className="flex-1"
                />
                {uploadingPhoto && <span className="text-sm text-gray-500">Uploading...</span>}
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3">
                {profile.photos.map((photo, idx) => (
                  <div 
                    key={idx} 
                    className={`relative group cursor-move ${draggedPhotoIdx === idx ? 'opacity-50' : ''}`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, idx)}
                    onDragOver={(e) => handleDragOver(e, idx)}
                    onDrop={(e) => handleDrop(e, idx)}
                    onDragEnd={handleDragEnd}
                  >
                    {/* Photo number badge */}
                    <div className="absolute top-1 left-1 z-10 bg-purple-600 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center shadow-lg">
                      {idx + 1}
                    </div>
                    
                    {/* Drag handle indicator */}
                    <div className="absolute top-1 left-1/2 -translate-x-1/2 z-10 bg-white/80 rounded px-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <GripVertical className="w-4 h-4 text-gray-600" />
                    </div>
                    
                    <img src={photo} alt={`Photo ${idx + 1}`} className="w-full h-32 object-cover rounded-lg border-2 border-transparent group-hover:border-purple-400 transition-all" />
                    
                    {/* Reorder buttons */}
                    <div className="absolute bottom-1 left-1 right-1 flex justify-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={() => movePhotoUp(idx)}
                        disabled={idx === 0}
                        className={`p-1 rounded ${idx === 0 ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'} text-white`}
                        title="Move up"
                      >
                        <ArrowUp className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => movePhotoDown(idx)}
                        disabled={idx === profile.photos.length - 1}
                        className={`p-1 rounded ${idx === profile.photos.length - 1 ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'} text-white`}
                        title="Move down"
                      >
                        <ArrowDown className="w-3 h-3" />
                      </button>
                    </div>
                    
                    {/* Delete button */}
                    <button
                      type="button"
                      onClick={() => removePhoto(photo)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 z-10"
                      style={{transition: 'opacity 0.2s ease'}}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              {profile.photos.length > 1 && (
                <p className="text-xs text-gray-500 mt-2 text-center">
                  💡 Drag photos or use arrows to reorder. Photo #1 will be your main profile picture.
                </p>
              )}
            </div>


            {/* Profile Video Section */}
            <div className="border-2 border-purple-200 rounded-xl p-4 bg-gradient-to-br from-purple-50 to-pink-50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <Video className="w-5 h-5 text-purple-600" />
                  <Label className="text-lg font-bold">Profile Video ✨</Label>
                </div>
                {videoUnlockStatus?.unlocked ? (
                  <div className="flex items-center space-x-1 bg-green-100 px-3 py-1 rounded-full">
                    <Unlock className="w-4 h-4 text-green-600" />
                    <span className="text-xs font-semibold text-green-700">Unlocked</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-1 bg-gray-100 px-3 py-1 rounded-full">
                    <Lock className="w-4 h-4 text-gray-600" />
                    <span className="text-xs font-semibold text-gray-700">Locked</span>
                  </div>
                )}
              </div>

              {videoUnlockStatus?.unlocked ? (
                <>
                  <p className="text-sm text-gray-600 mb-3">
                    📹 Upload a 5-second video to stand out! This will appear first on your profile.
                  </p>
                  
                  {profileVideoUrl ? (
                    <div className="space-y-3">
                      <div className="relative rounded-lg overflow-hidden" style={{height: '300px'}}>
                        <ProfileVideoPlayer 
                          videoUrl={profileVideoUrl.startsWith('http') ? profileVideoUrl : `${BACKEND_URL}${profileVideoUrl}`}
                          className="h-full"
                        />
                      </div>
                      <Button
                        type="button"
                        onClick={handleDeleteVideo}
                        variant="destructive"
                        className="w-full"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Video
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Input
                        type="file"
                        accept="video/mp4,video/webm,video/quicktime"
                        onChange={handleVideoUpload}
                        disabled={uploadingVideo}
                        className="cursor-pointer"
                      />
                      {uploadingVideo && (
                        <div className="text-center py-2">
                          <div className="inline-block animate-spin rounded-full h-6 w-6 border-4 border-purple-500 border-t-transparent"></div>
                          <p className="text-sm text-gray-600 mt-1">Uploading video...</p>
                        </div>
                      )}
                      <p className="text-xs text-gray-500">
                        💡 Tip: Keep it under 5 seconds. Max 20MB. MP4 or WebM format.
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-6">
                  <Lock className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm font-semibold text-gray-700 mb-2">
                    Get {videoUnlockStatus?.required_referrals || 5} referrals to unlock Profile Video!
                  </p>
                  <div className="bg-white rounded-lg p-3 mb-3">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs text-gray-600">Progress</span>
                      <span className="text-xs font-bold text-purple-600">
                        {videoUnlockStatus?.referral_count || 0}/{videoUnlockStatus?.required_referrals || 5}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-500"
                        style={{width: `${videoUnlockStatus?.progress_percentage || 0}%`}}
                      ></div>
                    </div>
                  </div>
                  <Button
                    type="button"
                    onClick={() => navigate('/settings')}
                    variant="outline"
                    className="text-purple-600 border-purple-300"
                  >
                    Share Referral Link
                  </Button>
                </div>
              )}
            </div>


            <div>
              <Label>Date Place Ideas 💕</Label>
              <p className="text-xs text-gray-500 mt-1 mb-2">Upload photos of places you'd love to go on a date</p>
              <div className="flex gap-2">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleDatePlacePhotoUpload}
                  disabled={uploadingDatePlace}
                  data-testid="date-place-photo-upload-input"
                  className="flex-1"
                />
                {uploadingDatePlace && <span className="text-sm text-gray-500">Uploading...</span>}
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3">
                {profile.date_place_photos.map((photo, idx) => (
                  <div key={idx} className="relative group">
                    <img src={photo} alt="Date Place" className="w-full h-32 object-cover rounded-lg" />
                    <button
                      type="button"
                      onClick={() => removeDatePlacePhoto(photo)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100"
                      style={{transition: 'opacity 0.2s ease'}}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Countries I Want to Visit 🌍</Label>
              <p className="text-xs text-gray-500 mt-1 mb-2">Select countries you'd love to explore</p>
              <div className="relative">
                <Button
                  type="button"
                  onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                  data-testid="add-country-btn"
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white rounded-full mb-3"
                >
                  <Globe className="w-4 h-4 mr-2" />
                  Add Country
                </Button>
                
                {showCountryDropdown && (
                  <div className="absolute z-10 w-full bg-white border-2 border-blue-300 rounded-xl shadow-xl max-h-60 overflow-y-auto">
                    {COUNTRIES.filter(c => !profile.countries_to_visit.includes(c)).map((country, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => addCountry(country)}
                        className="w-full text-left px-4 py-2 hover:bg-blue-50 border-b border-gray-100 text-sm"
                      >
                        {country}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex flex-wrap gap-2">
                {profile.countries_to_visit.map((country, idx) => (
                  <Badge key={idx} className="px-3 py-2 bg-blue-100 text-blue-700 hover:bg-blue-200 text-sm">
                    {country}
                    <X className="w-3 h-3 ml-2 cursor-pointer" onClick={() => removeCountry(country)} />
                  </Badge>
                ))}
              </div>
            </div>

            <Button
              type="submit"
              data-testid="save-profile-btn"
              disabled={loading}
              className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-full"
            >
              {loading ? 'Saving...' : 'Save & Start Matching'}
            </Button>

            {currentUserId && (
              <Button
                type="button"
                data-testid="preview-profile-btn"
                onClick={() => navigate(`/profile/${currentUserId}`)}
                variant="outline"
                className="w-full py-6 text-lg font-semibold border-2 border-purple-300 text-purple-700 hover:bg-purple-50 rounded-full mt-3"
              >
                <Eye className="w-5 h-5 mr-2" />
                Preview My Profile
              </Button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfileSetup;
